import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import AdminLayout from '../components/AdminLayout';
import './SuperAdmin.css';

export default function SuperAdmin() {
  const [contractors, setContractors] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [contractorUsers, setContractorUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialTarget, setTrialTarget] = useState(null);
  const [newTrialDate, setNewTrialDate] = useState('');
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState(null);

  // Load all contractors on mount
  useEffect(() => {
    loadContractors();
  }, []);

  async function loadContractors() {
    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase
        .rpc('get_all_contractors_admin');

      if (rpcError) throw rpcError;

      setContractors(data || []);
    } catch (err) {
      console.error('Error loading contractors:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadContractorUsers(contractorId) {
    try {
      setError(null);

      const { data, error: rpcError } = await supabase
        .rpc('get_contractor_users_admin', { p_contractor_id: contractorId });

      if (rpcError) throw rpcError;

      setContractorUsers(data || []);
      setSelectedContractor(contractorId);
    } catch (err) {
      console.error('Error loading contractor users:', err);
      setError(err.message);
    }
  }

  async function handleDeleteUser(userId, userName) {
    if (!confirm(`Are you sure you want to delete user "${userName}"? This cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      setError(null);

      const { data, error: rpcError } = await supabase
        .rpc('delete_user_admin', { p_user_id: userId });

      if (rpcError) throw rpcError;

      alert(`User "${userName}" deleted successfully.`);

      // Reload contractor users
      if (selectedContractor) {
        await loadContractorUsers(selectedContractor);
      }

      // Reload contractors list to update counts
      await loadContractors();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(`Error deleting user: ${err.message}`);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  function openDeleteModal(contractorId, companyName) {
    setDeleteTarget({ id: contractorId, name: companyName });
    setConfirmText('');
    setShowDeleteModal(true);
  }

  function closeDeleteModal() {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setConfirmText('');
  }

  async function confirmDelete() {
    if (confirmText !== 'CONFIRM') {
      return;
    }

    try {
      setDeleting(true);
      setError(null);

      console.log('[SuperAdmin] Deleting contractor:', deleteTarget.id);

      // Call the delete_contractor_admin RPC function (returns Stripe customer ID)
      const { data, error: rpcError } = await supabase
        .rpc('delete_contractor_admin', { p_contractor_id: deleteTarget.id });

      if (rpcError) {
        console.error('[SuperAdmin] RPC error:', rpcError);
        throw rpcError;
      }

      console.log('[SuperAdmin] Database deletion complete:', data);
      console.log('[SuperAdmin] Stripe customer ID from RPC:', data.stripe_customer_id);

      // If there was a Stripe customer, try to delete it via the webhook function
      let stripeDeleted = false;
      if (data.stripe_customer_id) {
        try {
          console.log('[SuperAdmin] Attempting to delete Stripe customer');
          const response = await fetch('/.netlify/functions/delete-contractor-stripe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              stripe_customer_id: data.stripe_customer_id,
              skip_database: true  // Database already deleted
            }),
          });

          if (response.ok) {
            stripeDeleted = true;
            console.log('[SuperAdmin] Stripe customer deleted');
          } else {
            console.warn('[SuperAdmin] Stripe deletion failed, but database cleanup succeeded');
          }
        } catch (stripeError) {
          console.warn('[SuperAdmin] Stripe deletion error:', stripeError);
          // Don't fail the whole operation if Stripe deletion fails
        }
      }

      // Close modal
      closeDeleteModal();

      // Show success message
      const message = `Company "${deleteTarget.name}" deleted successfully.\n\nDeleted:\n- ${data.deleted_users} users\n- ${data.deleted_assessments} assessments\n- ${data.deleted_system_type_settings} system type settings\n- ${data.deleted_system_options} system options\n- ${data.deleted_rebates} rebates${stripeDeleted ? '\n- Stripe customer account' : ''}`;

      alert(message);

      // Clear selected contractor if it was deleted
      if (selectedContractor === deleteTarget.id) {
        setSelectedContractor(null);
        setContractorUsers([]);
      }

      // Reload contractors list
      await loadContractors();
    } catch (err) {
      console.error('Error deleting contractor:', err);
      alert(`Error deleting contractor: ${err.message}`);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  function getDaysRemaining(trialEndsAt) {
    if (!trialEndsAt) return null;
    const now = new Date();
    const endDate = new Date(trialEndsAt);
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  function getTrialStatusColor(daysRemaining) {
    if (daysRemaining === null) return 'status-unknown';
    if (daysRemaining < 0) return 'status-expired';
    if (daysRemaining <= 3) return 'status-expiring-soon';
    if (daysRemaining <= 7) return 'status-expiring';
    return 'status-active';
  }

  function openTrialModal(contractor) {
    setTrialTarget(contractor);
    // Format current date for datetime-local input
    if (contractor.trial_ends_at) {
      const date = new Date(contractor.trial_ends_at);
      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      setNewTrialDate(localDate.toISOString().slice(0, 16));
    } else {
      // Default to 7 days from now
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 7);
      const localDate = new Date(defaultDate.getTime() - defaultDate.getTimezoneOffset() * 60000);
      setNewTrialDate(localDate.toISOString().slice(0, 16));
    }
    setShowTrialModal(true);
  }

  function closeTrialModal() {
    setShowTrialModal(false);
    setTrialTarget(null);
    setNewTrialDate('');
  }

  function setQuickDate(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    setNewTrialDate(localDate.toISOString().slice(0, 16));
  }

  async function handleUpdateTrialDate() {
    if (!trialTarget || !newTrialDate) return;

    try {
      setUpdating(true);
      setError(null);

      // Convert local datetime to UTC timestamp
      const trialDate = new Date(newTrialDate).toISOString();

      const { data, error: rpcError } = await supabase
        .rpc('update_trial_end_date_admin', {
          p_contractor_id: trialTarget.contractor_id,
          p_new_trial_end_date: trialDate
        });

      if (rpcError) throw rpcError;

      // Show success notification
      setNotification({
        type: 'success',
        message: `Trial date updated successfully for ${trialTarget.company_name}`
      });

      // Refresh contractors list
      await loadContractors();

      // Close modal
      closeTrialModal();

      // Clear notification after 3 seconds
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error('Error updating trial date:', error);
      setNotification({
        type: 'error',
        message: `Failed to update trial date: ${error.message}`
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setUpdating(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="super-admin-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading contractors...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="super-admin-container">
      <div className="super-admin-header">
        <h1>Super Admin Panel</h1>
        <p className="warning-text">
          ⚠️ Internal use only - Use with caution
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="super-admin-content">
        {/* Left Panel: Contractors List */}
        <div className="contractors-panel">
          <div className="panel-header">
            <h2>Companies ({contractors.length})</h2>
            <button
              className="btn-refresh"
              onClick={loadContractors}
              disabled={deleting}
            >
              🔄 Refresh
            </button>
          </div>

          {contractors.length === 0 ? (
            <div className="empty-state">
              <p>No companies found</p>
            </div>
          ) : (
            <div className="contractors-list">
              {contractors.map(contractor => (
                <div
                  key={contractor.contractor_id}
                  className={`contractor-card ${selectedContractor === contractor.contractor_id ? 'selected' : ''}`}
                >
                  <div
                    className="contractor-info"
                    onClick={() => loadContractorUsers(contractor.contractor_id)}
                  >
                    <div className="contractor-name">{contractor.company_name}</div>
                    <div className="contractor-meta">
                      <span className="owner-email">{contractor.owner_email}</span>
                      <span className="owner-name">{contractor.owner_name || 'No name'}</span>
                    </div>
                    <div className="contractor-stats">
                      <span className="stat">
                        👥 {contractor.team_member_count + 1} users
                      </span>
                      <span className={`stat status-${contractor.subscription_status || 'unknown'}`}>
                        {contractor.subscription_status || 'trial'}
                      </span>
                    </div>
                    <div className="contractor-date">
                      Created: {new Date(contractor.created_at).toLocaleDateString()}
                    </div>
                    {contractor.trial_ends_at && (
                      <div className={`contractor-trial ${getTrialStatusColor(getDaysRemaining(contractor.trial_ends_at))}`}>
                        Trial: {getDaysRemaining(contractor.trial_ends_at) > 0
                          ? `${getDaysRemaining(contractor.trial_ends_at)} days left`
                          : getDaysRemaining(contractor.trial_ends_at) === 0
                          ? 'Expires today'
                          : `Expired ${Math.abs(getDaysRemaining(contractor.trial_ends_at))} days ago`
                        }
                      </div>
                    )}
                  </div>

                  <div className="contractor-actions">
                    <button
                      className="btn-edit-trial"
                      onClick={(e) => {
                        e.stopPropagation();
                        openTrialModal(contractor);
                      }}
                      disabled={deleting || updating}
                    >
                      🔓 Edit Trial
                    </button>
                    <button
                      className="btn-delete-contractor"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(contractor.contractor_id, contractor.company_name);
                      }}
                      disabled={deleting || updating}
                    >
                      🗑️ Delete Company
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Panel: Users List */}
        <div className="users-panel">
          <div className="panel-header">
            <h2>
              {selectedContractor
                ? `Users (${contractorUsers.length})`
                : 'Select a company to view users'}
            </h2>
          </div>

          {!selectedContractor ? (
            <div className="empty-state">
              <p>← Click a company on the left to view its users</p>
            </div>
          ) : contractorUsers.length === 0 ? (
            <div className="empty-state">
              <p>No users found for this company</p>
            </div>
          ) : (
            <div className="users-list">
              {contractorUsers.map(user => (
                <div
                  key={user.user_id}
                  className={`user-card ${user.is_owner ? 'owner' : ''}`}
                >
                  <div className="user-info">
                    <div className="user-name">
                      {user.name || 'No name'}
                      {user.is_owner && <span className="owner-badge">OWNER</span>}
                    </div>
                    <div className="user-email">{user.email}</div>
                    <div className="user-meta">
                      <span className="user-role">{user.role}</span>
                      <span className="user-date">
                        Joined: {new Date(user.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {!user.is_owner && (
                    <button
                      className="btn-delete-user"
                      onClick={() => handleDeleteUser(user.user_id, user.name || user.email)}
                      disabled={deleting}
                    >
                      🗑️ Delete User
                    </button>
                  )}

                  {user.is_owner && (
                    <div className="owner-note">
                      Delete company to remove owner
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚠️ Delete Company</h2>
              <button className="modal-close" onClick={closeDeleteModal}>×</button>
            </div>

            <div className="modal-body">
              <p className="modal-warning">
                You are about to permanently delete <strong>{deleteTarget.name}</strong> and ALL associated data:
              </p>

              <ul className="modal-delete-list">
                <li>All users (owner and team members)</li>
                <li>All assessments</li>
                <li>All system types and options</li>
                <li>All rebates</li>
                <li>All company settings</li>
                <li>Stripe customer account</li>
              </ul>

              <p className="modal-warning-strong">
                This action CANNOT be undone.
              </p>

              <div className="modal-confirm-input">
                <label htmlFor="confirm-input">
                  Type <strong>CONFIRM</strong> to proceed:
                </label>
                <input
                  id="confirm-input"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="CONFIRM"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && confirmText === 'CONFIRM') {
                      confirmDelete();
                    }
                  }}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button
                className="btn-delete"
                onClick={confirmDelete}
                disabled={confirmText !== 'CONFIRM' || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Company'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trial Edit Modal */}
      {showTrialModal && trialTarget && (
        <div className="modal-overlay" onClick={closeTrialModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔓 Edit Trial Period</h2>
              <button className="modal-close" onClick={closeTrialModal} disabled={updating}>×</button>
            </div>

            <div className="modal-body">
              <div className="trial-modal-company">
                Company: <strong>{trialTarget.company_name}</strong>
              </div>

              {trialTarget.trial_ends_at && (
                <div className="trial-current-info">
                  <div className="trial-label">Current Trial End Date:</div>
                  <div className="trial-date">
                    {new Date(trialTarget.trial_ends_at).toLocaleDateString()} at {new Date(trialTarget.trial_ends_at).toLocaleTimeString()}
                  </div>
                  <div className={`trial-status ${getTrialStatusColor(getDaysRemaining(trialTarget.trial_ends_at))}`}>
                    {getDaysRemaining(trialTarget.trial_ends_at) > 0
                      ? `${getDaysRemaining(trialTarget.trial_ends_at)} days remaining`
                      : getDaysRemaining(trialTarget.trial_ends_at) === 0
                      ? 'Expires today'
                      : `Expired ${Math.abs(getDaysRemaining(trialTarget.trial_ends_at))} days ago`
                    }
                  </div>
                </div>
              )}

              <div className="trial-quick-presets">
                <label>Quick Presets:</label>
                <div className="preset-buttons">
                  <button onClick={() => setQuickDate(7)} disabled={updating}>+7 days</button>
                  <button onClick={() => setQuickDate(30)} disabled={updating}>+30 days</button>
                  <button onClick={() => setQuickDate(90)} disabled={updating}>+90 days</button>
                </div>
              </div>

              <div className="trial-date-input">
                <label htmlFor="new-trial-date">New Trial End Date:</label>
                <input
                  id="new-trial-date"
                  type="datetime-local"
                  value={newTrialDate}
                  onChange={(e) => setNewTrialDate(e.target.value)}
                  disabled={updating}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeTrialModal} disabled={updating}>
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleUpdateTrialDate}
                disabled={updating || !newTrialDate}
              >
                {updating ? 'Updating...' : 'Update Trial Date'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`notification-toast ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-icon">
              {notification.type === 'success' ? '✓' : '✗'}
            </span>
            <span className="notification-message">{notification.message}</span>
            <button
              className="notification-close"
              onClick={() => setNotification(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
